import { claudeModelTier } from '@/features/llm-profiles/claudeModelCatalog';
import { formatTokens } from '@/features/workspace-usage/money';
import type {
  AIModelEntry,
  AIModelUse,
  AIModelWorkflow,
  LLMProfileView,
  PeriodBounds,
} from '@/lib/api/generated/model';
import { noRunsLine, tokenTotal, workTokens } from './aiModelsCopy';

/**
 * The Models tab's ledger and step map (spec 233), derived in the browser from one
 * read: `entries` for the profiles, service 173's `workflows` for the map. Pure, so
 * every rule the page draws is tested here rather than through the DOM.
 */

/**
 * The key of the runs that named no profile. A deleted profile is not unknown: it
 * keeps its own line, key and name (spec 258).
 */
export const UNKNOWN_PROFILE_KEY = 'unknown';
/**
 * A data gap, not a profile (spec 252): grey, hollow, last, never counted among the
 * profiles, with one line saying why.
 */
export const UNKNOWN_PROFILE_NAME = 'Not recorded';
export const UNKNOWN_PROFILE_HINT = 'Runs from before profiles were recorded.';
/** Under a deleted profile's name, where a live one states its effort. */
export const DELETED_PROFILE_HINT =
  'Deleted. Its runs still count here, under its last name.';

/**
 * One of the five brand colours (spec 251), `'other'` for a sixth profile and
 * beyond (grey, with a dot), or `'unknown'` for the runs whose profile is not known
 * (grey, no dot).
 */
export type IdentitySlot = 1 | 2 | 3 | 4 | 5 | 'other' | 'unknown';

export interface ProfileIdentity {
  key: string;
  slot: IdentitySlot;
  /** The model family's initial ("S"), or null for a model the catalog does not know. */
  letter: string | null;
  /**
   * Configured but not run in the period (spec 252): its dot is a ring, so an idle
   * profile does not colour the map as a busy one does.
   */
  idle: boolean;
}

const SLOTS = 5;

/**
 * A profile's key: its slug, the one key the entries and the map share (R1). A
 * deleted profile's is the key its runs recorded (spec 258).
 */
export function profileKey(entry: AIModelEntry): string {
  return (
    entry.profile?.slug ?? entry.deleted_profile?.key ?? UNKNOWN_PROFILE_KEY
  );
}

/** The entry's name: the live profile's, the deleted one's last, or "Not recorded". */
export function profileName(entry: AIModelEntry): string {
  return (
    entry.profile?.name ?? entry.deleted_profile?.name ?? UNKNOWN_PROFILE_NAME
  );
}

/** The runs that named no profile: neither a live nor a deleted one. */
export function isUnknownEntry(entry: AIModelEntry): boolean {
  return entry.profile === null && entry.deleted_profile === null;
}

/** The model family's initial ("S" for Sonnet), or null for a model the catalog does not know. */
export function familyLetter(model: string): string | null {
  return claudeModelTier(model)?.charAt(0) ?? null;
}

function letterOf(profile: LLMProfileView): string | null {
  return familyLetter(profile.model);
}

/**
 * Each profile's identity colour for the period (spec 251): slots go by runs, most
 * first, ties by name, so the profile doing most of the work is always brand blue.
 * Profiles that did not run follow, by name. Five brand colours, then grey: past
 * five, the letter and the name beside every mark keep rows apart.
 */
export function profileIdentities(
  entries: AIModelEntry[],
): Map<string, ProfileIdentity> {
  const identities = new Map<string, ProfileIdentity>();
  // A deleted profile ranks among the others by its runs (spec 258 FR-002): it ran,
  // so it keeps a colour. Its letter comes from the model its runs used.
  const ranked = entries
    .filter((entry) => !isUnknownEntry(entry))
    .sort(
      (a, b) =>
        b.runs.run_count - a.runs.run_count ||
        profileName(a).localeCompare(profileName(b)) ||
        profileKey(a).localeCompare(profileKey(b)),
    );
  for (const entry of ranked) {
    const key = profileKey(entry);
    if (identities.has(key)) continue;
    const place = identities.size;
    const model = entry.by_model[0]?.model_key;
    identities.set(key, {
      key,
      slot: place < SLOTS ? ((place + 1) as IdentitySlot) : 'other',
      letter: entry.profile
        ? letterOf(entry.profile)
        : model
          ? familyLetter(model)
          : null,
      idle: entries.every(
        (other) => profileKey(other) !== key || other.runs.run_count === 0,
      ),
    });
  }
  if (entries.some(isUnknownEntry)) {
    identities.set(UNKNOWN_PROFILE_KEY, {
      key: UNKNOWN_PROFILE_KEY,
      slot: 'unknown',
      letter: null,
      idle: false,
    });
  }
  return identities;
}

/** The unknown's identity, for a caller that meets a key the map does not hold. */
const UNKNOWN_IDENTITY: ProfileIdentity = {
  key: UNKNOWN_PROFILE_KEY,
  slot: 'unknown',
  letter: null,
  idle: false,
};

export function identityOf(
  identities: Map<string, ProfileIdentity>,
  key: string,
): ProfileIdentity {
  return identities.get(key) ?? UNKNOWN_IDENTITY;
}

/** One profile's row in the ledger (spec 233 US2). */
export interface LedgerRow {
  key: string;
  name: string;
  /** A deleted profile's runs: its last name, no settings, no uses (spec 258). */
  deleted: boolean;
  entry: AIModelEntry;
  profile: LLMProfileView | null;
  /** Null when the page had no runs at all: the row reads "—". */
  runs: number | null;
  unmeasured: number;
  /** Null when there is nothing to count: no runs on the page, or no token counts. */
  workTokens: number | null;
  uses: AIModelUse[];
  usedBy: number;
  /** Runs over the page's highest row, 0–1: one scale for every runs bar. */
  barShare: number;
  /** Place among the known profiles, most runs first (0 leads); null for the unknown. */
  rank: number | null;
  identity: ProfileIdentity;
}

/** Runs first, then name; the unknown always last, whatever it ran. */
export function ledgerRows(
  entries: AIModelEntry[],
  identities: Map<string, ProfileIdentity>,
): LedgerRow[] {
  const pageRuns = entries.reduce((sum, e) => sum + e.runs.run_count, 0);
  const highest = Math.max(0, ...entries.map((e) => e.runs.run_count));
  const rows = entries.map((entry): LedgerRow => {
    const key = profileKey(entry);
    const { runs } = entry;
    return {
      key,
      name: profileName(entry),
      deleted: entry.profile === null && entry.deleted_profile !== null,
      entry,
      profile: entry.profile,
      runs: pageRuns === 0 ? null : runs.run_count,
      unmeasured: runs.unmeasured_run_count,
      workTokens:
        pageRuns === 0 || tokenTotal(runs) === 0 ? null : workTokens(runs),
      uses: entry.uses,
      usedBy: entry.uses.length,
      barShare: highest === 0 ? 0 : runs.run_count / highest,
      rank: null,
      identity: identityOf(identities, key),
    };
  });
  return rows
    .sort((a, b) => {
      if (isUnknownEntry(a.entry)) return 1;
      if (isUnknownEntry(b.entry)) return -1;
      const byRuns = (b.runs ?? 0) - (a.runs ?? 0);
      return byRuns !== 0 ? byRuns : a.name.localeCompare(b.name);
    })
    .map((row, index) => ({
      ...row,
      rank: isUnknownEntry(row.entry) ? null : index,
    }));
}

/** One segment of the Runs by profile bar (spec 233 US1). */
export interface ShareSegment {
  key: string;
  name: string;
  runs: number;
  /** 0–1, for the segment's width. */
  share: number;
  /** Whole percent for the legend; the page's segments sum to 100. */
  percent: number;
  /** The row's rank, for the quantity colour (spec 251). */
  rank: number | null;
  identity: ProfileIdentity;
}

/**
 * The bar's segments, in ledger order with the unknown last, profiles that did not
 * run left out. Percents by largest remainder, so the legend never reads 99 or 101.
 */
export function shareSegments(
  entries: AIModelEntry[],
  identities: Map<string, ProfileIdentity>,
): ShareSegment[] {
  const rows = ledgerRows(entries, identities).filter(
    (row) => (row.runs ?? 0) > 0,
  );
  const total = rows.reduce((sum, row) => sum + (row.runs ?? 0), 0);
  if (total === 0) return [];
  const exact = rows.map((row) => ((row.runs ?? 0) / total) * 100);
  const percents = exact.map(Math.floor);
  let left = 100 - percents.reduce((sum, p) => sum + p, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, rest: value - Math.floor(value) }))
    .sort((a, b) => b.rest - a.rest);
  for (const { index } of byRemainder) {
    if (left === 0) break;
    percents[index] += 1;
    left -= 1;
  }
  return rows.map((row, index) => ({
    key: row.key,
    name: row.name,
    runs: row.runs ?? 0,
    share: (row.runs ?? 0) / total,
    percent: percents[index],
    rank: row.rank,
    identity: row.identity,
  }));
}

const plural = (count: number, one: string, many: string) =>
  `${String(count)} ${count === 1 ? one : many}`;

/** "No runs", "1 run", "14 runs". */
export function runsCount(count: number): string {
  return count === 0 ? 'No runs' : plural(count, 'run', 'runs');
}

/**
 * The card's one line (FR-003): "41 runs · 11.5M work tokens (input and output) · 3
 * profiles in 9 workflows", or the quiet month in place of the figures. Workflows are
 * the distinct ones across every profile's uses; the unknown is not a profile. The
 * figure says what it counts (spec 256): Models asks what the models did, so its tokens
 * are the work; Spend asks what was billed and says "billed tokens, with cache".
 */
export function summaryLine(
  entries: AIModelEntry[],
  period: PeriodBounds,
): string {
  const runs = entries.reduce((sum, e) => sum + e.runs.run_count, 0);
  const tokens = entries.reduce((sum, e) => sum + workTokens(e.runs), 0);
  const profiles = entries.filter((e) => e.profile !== null).length;
  const workflows = new Set(
    entries.flatMap((e) => e.uses.map((u) => u.workflow_id)),
  ).size;
  const where = `${plural(profiles, 'profile', 'profiles')} in ${plural(workflows, 'workflow', 'workflows')}`;
  if (runs === 0) return `${noRunsLine(period)} · ${where}`;
  return `${plural(runs, 'run', 'runs')} · ${formatTokens(tokens)} work tokens (input and output) · ${where}`;
}

/** What a map chip is (research R4). */
export type MapStepKind = 'profile' | 'default' | 'approval' | 'linked';

export interface MapStep {
  id: string;
  name: string;
  order: number;
  unpublished: boolean;
  kind: MapStepKind;
  /** The profile's key for a `profile` chip; null otherwise. */
  profileKey: string | null;
}

/** One workflow of the step map (spec 233 US3). */
export interface MapRow {
  workflowId: string;
  key: string;
  name: string;
  draft: boolean;
  runs: number;
  unmeasured: number;
  steps: MapStep[];
}

/**
 * The map's rows from service 173's `workflows` (missing → none), in the service's
 * order (runs, then name). An AI step whose slug names no profile on the page runs on
 * the default model; a human gate runs none, and a linked workflow runs none unless it
 * names a profile (its fan-out agent runs there).
 */
export function mapRows(
  workflows: AIModelWorkflow[] | undefined,
  identities: Map<string, ProfileIdentity>,
): MapRow[] {
  return (workflows ?? []).map((workflow) => ({
    workflowId: workflow.workflow_id,
    key: workflow.workflow_key,
    name: workflow.workflow_name,
    draft: workflow.draft,
    runs: workflow.runs.run_count,
    unmeasured: workflow.runs.unmeasured_run_count,
    steps: [...workflow.steps]
      .sort((a, b) => a.order - b.order)
      .map((step): MapStep => {
        const known =
          step.profile_slug !== null && identities.has(step.profile_slug);
        // A linked workflow on a profile runs its fan-out agent there, so it wears
        // the profile's mark like an AI step; without one it runs no model.
        const kind: MapStepKind =
          step.type === 'human_gate'
            ? 'approval'
            : known
              ? 'profile'
              : step.type === 'linked_workflow'
                ? 'linked'
                : 'default';
        return {
          id: step.id,
          name: step.name,
          order: step.order,
          unpublished: step.unpublished,
          kind,
          profileKey: kind === 'profile' ? step.profile_slug : null,
        };
      }),
  }));
}

/**
 * The map split for reading (spec 252): workflows that ran, by runs (the service's
 * order), and those that did not, folded into one row.
 */
export function splitMapRows(rows: MapRow[]): {
  ran: MapRow[];
  idle: MapRow[];
} {
  return {
    ran: rows.filter((row) => row.runs > 0),
    idle: rows.filter((row) => row.runs === 0),
  };
}

/** The map's search (spec 230 FR-008): the workflow's name, trimmed and case-folded. */
export function matchesMapRow(row: MapRow, needle: string): boolean {
  const query = needle.trim().toLocaleLowerCase();
  if (query === '') return true;
  return row.name.toLocaleLowerCase().includes(query);
}
